import React, { useEffect, useState } from 'react';
import { api } from './AdminApp';
import Loader from '../Components/Loader/Loader';

function fmtTime(v) {
  if (!v) return '—';
  const d = new Date(String(v).replace(' ', 'T'));
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

const ACTION_STYLES = {
  login: 'admin-status-completed',
  logout: 'admin-status-muted',
  'login failed': 'admin-status-lead',
  'user create': 'admin-status-completed',
  'user delete': 'admin-status-lead',
  'user password reset': 'admin-status-progress',
};

export default function Logs() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async (opts = {}) => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      q.set('page', String(opts.page ?? page));
      q.set('per_page', String(opts.perPage ?? perPage));
      const s = opts.search !== undefined ? opts.search : search;
      if (s) q.set('search', s);
      const u = opts.username !== undefined ? opts.username : userFilter;
      if (u) q.set('username', u);
      const d = await api(`/admin/logs?${q.toString()}`);
      setItems(d.items || []);
      setUsers(d.users || []);
      setPage(d.page || 1);
      setTotal(d.total || 0);
      setTotalPages(d.total_pages || 1);
      setError('');
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    load({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
    load({ page: 1, search: searchInput });
  };

  const goto = (p) => {
    if (p < 1 || p > totalPages || p === page) return;
    load({ page: p });
  };

  const changePerPage = (n) => {
    setPerPage(n);
    setPage(1);
    load({ page: 1, perPage: n });
  };

  const filterUser = (u) => {
    setUserFilter(u);
    setPage(1);
    load({ page: 1, username: u });
  };

  const pageNumbers = () => {
    const out = [];
    for (let p = 1; p <= totalPages; p++) {
      if (p === 1 || p === totalPages || Math.abs(p - page) <= 2) out.push(p);
      else if (out[out.length - 1] !== '…') out.push('…');
    }
    return out;
  };

  return (
    <div>
      <h1 className="admin-page-title">Activity Logs</h1>
      <p className="admin-muted" style={{ marginTop: -10 }}>
        Every change made in the admin panel: who, what, when, and from which IP.
      </p>
      {error && <div className="admin-alert admin-alert-error">{error}</div>}

      <div className="admin-leads-toolbar">
        <form className="admin-leads-search" onSubmit={doSearch}>
          <input
            placeholder="Search action, details, IP…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <button type="submit" className="admin-btn">Search</button>
          {search && (
            <button
              type="button"
              className="admin-btn"
              onClick={() => {
                setSearchInput('');
                setSearch('');
                setPage(1);
                load({ page: 1, search: '' });
              }}
            >
              ×
            </button>
          )}
        </form>

        <div className="admin-leads-filters">
          <span className="admin-muted" style={{ fontSize: 13 }}>User:</span>
          <button
            className={`admin-chip ${userFilter === '' ? 'active' : ''}`}
            onClick={() => filterUser('')}
          >
            All
          </button>
          {users.map((u) => (
            <button
              key={u}
              className={`admin-chip ${userFilter === u ? 'active' : ''}`}
              onClick={() => filterUser(u)}
            >
              {u}
            </button>
          ))}
        </div>

        <div className="admin-leads-count">{loading ? '…' : `${total} entries`}</div>
      </div>

      {loading ? (
        <Loader inline status="Loading activity logs" />
      ) : items.length === 0 ? (
        <p className="admin-muted">No log entries yet.</p>
      ) : (
        <>
          <table className="admin-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Time</th>
                <th>User</th>
                <th>Action</th>
                <th>Details</th>
                <th>IP address</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id}>
                  <td>{r.id}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmtTime(r.created_at)}</td>
                  <td><strong>{r.username}</strong></td>
                  <td>
                    <span
                      className={`admin-status ${ACTION_STYLES[r.action] || 'admin-status-progress'}`}
                      style={{ cursor: 'default' }}
                    >
                      {r.action}
                    </span>
                  </td>
                  <td title={r.details || ''}>
                    {(r.details || '—').length > 70
                      ? `${r.details.slice(0, 70)}…`
                      : r.details || '—'}
                  </td>
                  <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.ip_address || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="admin-pagination">
            <button
              className="admin-btn admin-btn-sm"
              disabled={page <= 1}
              onClick={() => goto(page - 1)}
            >
              ← Prev
            </button>
            {pageNumbers().map((p, i) =>
              p === '…' ? (
                <span key={`e${i}`} className="admin-page-ellipsis">…</span>
              ) : (
                <button
                  key={p}
                  className={`admin-page-btn ${p === page ? 'active' : ''}`}
                  onClick={() => goto(p)}
                >
                  {p}
                </button>
              )
            )}
            <button
              className="admin-btn admin-btn-sm"
              disabled={page >= totalPages}
              onClick={() => goto(page + 1)}
            >
              Next →
            </button>

            <select
              className="admin-perpage"
              value={perPage}
              onChange={(e) => changePerPage(Number(e.target.value))}
            >
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n} / page
                </option>
              ))}
            </select>
          </div>
        </>
      )}
    </div>
  );
}
